/* eslint-disable @typescript-eslint/no-use-before-define */
import { useEffect, useMemo, useState, Fragment } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { ShareIcon, ChevronDownIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/20/solid";
import { Menu, Transition } from "@headlessui/react";

// Static Assets.
import { useDeviceType, useRouter } from "components/hooks";
import { getMagnetPageLink } from "utils/helpers";
import LinkedIn from "assets/Logos/Linkedin.png";
import Twitter from "assets/Logos/Twitter.png";
import DefaultImage from "assets/Images/BrandImage1.png";

// Components.

import UserMagnetMementosModal from "components/molecules/user/UserMagnetMementosModal";
import BlurredImage from "components/atoms/BlurredImage";
import DummyProfileImage from "assets/Images/DummyProfileImage.svg";
import { Tab } from "components/atoms/Tab";
import { useUserContext } from "components/context/UserContext";
import MagnetCard from "components/atoms/cards/user/MagnetAndMementoCard";

import Footer from "components/pageComponents/Footer/Footer";
import EditFeaturedMagnetsModal from "components/organisms/user/EditFeaturedModal";
import ActivityPanel from "components/molecules/user/ActivityPanel";

import { Helmet } from "react-helmet-async";
import { linkShare } from "utils/HelperFunctions";
import AuthorizeButton from "components/organisms/AuthorizeButton";
import IncentivesPanel from "components/molecules/user/IncentivesPanel";
import { REDIRECT_TO_TWITTER, REDIRECT_TO_LINKEDIN } from "../../constants";
import { useWalkthroughContext } from "./walkthrough/WalkthroughContext";
import Fridge from "./Fridge";

type Props = {
  userDetails: User;
  isPublicProfile?: boolean;
};

function UserProfile({ userDetails, isPublicProfile = true }: Props) {
  const location = useLocation();
  const isHomepage = location.pathname === "/";

  if (!userDetails) return null;
  const [isFeaturedModalOpen, setFeaturedModalOpen] = useState(false);
  const router = useRouter();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { memento_id } = router.query;
  const [magnetIdForMementoModal, setMagnetIdForMementoModal] = useState<string | null>(null);

  const { setCurrentlyViewingMementoId } = useUserContext();

  const [, setSearchParams] = useSearchParams();

  const { isMobile } = useDeviceType();

  useEffect(() => {
    if (memento_id) {
      setCurrentlyViewingMementoId(memento_id);
      setSearchParams(
        {},
        {
          replace: true,
        },
      );
    }
  }, [memento_id]);

  const invItemDataForMagnetNotificationModal = useMemo(() => {
    if (!magnetIdForMementoModal) return null;
    return userDetails.inventory.find((inv) => inv.object_id === magnetIdForMementoModal);
  }, [userDetails.inventory, magnetIdForMementoModal]);

  const handleInventoryItemClick = (inventoryId: string) => {
    const invItem = userDetails.inventory.find((inv) => inv.id === inventoryId) as InventoryItem;

    if (invItem.type === "MEMENTO") {
      if (isPublicProfile) return;
      setCurrentlyViewingMementoId(invItem.object_id);
    }
    if (invItem.type === "MAGNET") {
      if (isPublicProfile) {
        router.go(getMagnetPageLink(invItem.brand_id, invItem.object_id));
      } else {
        setMagnetIdForMementoModal(invItem.object_id);
      }
    }
  };

  const { walkthroughData, setWalkthroughRun } = useWalkthroughContext();

  useEffect(() => {
    if (walkthroughData.tourActive) setWalkthroughRun(true);
  }, [walkthroughData.tourActive]);

  return (
    <>
      <Helmet>
        <title>{isHomepage ? "Home - MAGNETIQ" : `${userDetails.username}'s Profile - MAGNETIQ`}</title>
      </Helmet>
      {isMobile ? (
        <div className="mx-4 my-3 flex flex-col">
          <div className="flex flex-col items-start justify-between text-white ">
            <BlurredImage
              imageHash={userDetails.profile_image_hash}
              src={userDetails.profile_image_url || DummyProfileImage}
              imageContainerClasses="h-28 w-28 rounded-full object-cover  drop-shadow-2xl 2xl:h-40 2xl:w-40"
              alt={userDetails.username}
            />
            <div className="inline-flex w-full items-center justify-between gap-x-4">
              <div className=" truncate text-ellipsis ">
                <p className=" text-xs font-bold sm:text-lg md:text-xl">{userDetails.username}</p>
              </div>
              <ShareDropDown userName={userDetails.username} />
            </div>
          </div>
          <div className="">
            <Tab
              borderWhite
              fullBorder
              fullWidthTabs
              className="mt-4 w-full justify-between border-b border-white border-opacity-40"
            >
              <Tab.TabPane
                key="Fridge"
                tab="Fridge"
                className="user-fridge-container relative pb-1 pt-3 text-center font-satoshi text-sm"
              >
                <div className="mt-4 sm:mt-8">
                  <Fridge
                    featuredMagnets={userDetails.featured}
                    handleInventoryItemClick={handleInventoryItemClick}
                    isPublicProfile={isPublicProfile}
                    setFeaturedModalOpen={setFeaturedModalOpen}
                    isMobile
                  />
                </div>
              </Tab.TabPane>
              <Tab.TabPane
                key="Activity"
                tab="Activity"
                className="relative pb-1 pt-3 text-center font-satoshi text-sm"
              >
                <div className="mt-4">
                  <ActivityPanel isPublicProfile={!!isPublicProfile} />
                </div>
              </Tab.TabPane>
              <Tab.TabPane key="Magnets" tab="Magnets" className="relative pb-1 pt-3 text-center font-satoshi  text-sm">
                <p className="py-3 font-bold text-white">My Magnets</p>
                <div className="my-2 grid grid-cols-2 gap-x-6  gap-y-4 sm:grid-cols-3 ">
                  {userDetails.inventory.map(
                    (magnet) =>
                      magnet.type === "MAGNET" && (
                        <MagnetCard
                          disabled={!magnet.is_purchase_verified}
                          isClickable
                          key={`featured${magnet.id}_mag`}
                          name={magnet.name}
                          imageUrl={magnet.image_url || DefaultImage}
                          brandName={magnet.brand_name}
                          cardAdditionalClasses="magnet-card-background !p-2"
                          handleOnClick={() => {
                            if (isPublicProfile) router.go(getMagnetPageLink(magnet.brand_id, magnet.object_id));
                            else setMagnetIdForMementoModal(magnet.object_id);
                          }}
                        />
                      ),
                  )}
                </div>
              </Tab.TabPane>
              <Tab.TabPane
                key="Incentives"
                tab="Incentives"
                className="relative pb-1 pt-3 text-center font-satoshi text-sm"
              >
                {!isPublicProfile && <IncentivesPanel />}
              </Tab.TabPane>
            </Tab>
          </div>
        </div>
      ) : (
        // Desktop view
        <>
          <div className="bg-midnight-blue  min-h-[88vh]">
            <div className="grid grid-cols-12 font-satoshi text-xl font-medium leading-6 text-white  2xl:py-6 2xl:text-2xl">
              <p className="col-span-7 mx-8 py-4 3xl:mx-16">{userDetails.username}&apos;s Fridge</p>
              <span className="col-span-5 lg:px-4">
                <div className="float-right flex items-center gap-5">
                  {!userDetails.has_authorized_twitter && <AuthorizeButton authorizationFor="twitter" />}
                  <ShareDropDown userName={userDetails.username} />
                </div>
              </span>
            </div>
            <div className="grid grid-cols-12 py-1 2xl:py-2">
              <section className="user-fridge-container col-span-7 px-8">
                <Fridge
                  featuredMagnets={userDetails.featured}
                  handleInventoryItemClick={handleInventoryItemClick}
                  isPublicProfile={isPublicProfile}
                  setFeaturedModalOpen={setFeaturedModalOpen}
                />
              </section>
              <section className="col-span-5 col-start-8 flex h-[105vh] flex-col gap-2 lg:px-4">
                {!isPublicProfile && <IncentivesPanel />}
                <ActivityPanel isPublicProfile={!!isPublicProfile} />
              </section>
            </div>
          </div>
          <div className="mt-12">
            <Footer />
          </div>
        </>
      )}

      {!!magnetIdForMementoModal && invItemDataForMagnetNotificationModal && (
        <UserMagnetMementosModal
          magnetId={invItemDataForMagnetNotificationModal.object_id}
          magnetImage={invItemDataForMagnetNotificationModal.image_url || DefaultImage}
          magnetName={invItemDataForMagnetNotificationModal.name}
          brandName={invItemDataForMagnetNotificationModal.brand_name}
          brandId={invItemDataForMagnetNotificationModal.brand_id}
          isNotificationModalOpen={!!magnetIdForMementoModal}
          onNotificationModalClose={() => {
            setMagnetIdForMementoModal(null);
          }}
          handleGoToMagnetPage={() =>
            router.go(
              getMagnetPageLink(
                invItemDataForMagnetNotificationModal.brand_id,
                invItemDataForMagnetNotificationModal.object_id,
              ),
            )
          }
        />
      )}

      {!isPublicProfile && (
        <EditFeaturedMagnetsModal
          open={isFeaturedModalOpen}
          setOpen={setFeaturedModalOpen}
          inventory={userDetails.inventory}
        />
      )}
    </>
  );
}

function ShareDropDown({ userName }: { userName: string }) {
  return (
    <Menu as="div" className="relative z-30 inline-block text-left">
      <div>
        <Menu.Button className="hover inline-flex items-center gap-x-1.5 rounded-full border-2 border-neutral-300 bg-transparent px-4 py-1.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:bg-white active:bg-opacity-20">
          <ShareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          <p className="mb-0.5 font-satoshi font-bold text-white xl:text-xxs 2xl:text-xs">Share</p>
          <ChevronDownIcon className="-mx-1 mt-0.5 h-3 w-3 sm:h-4 sm:w-4" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="drop absolute right-0 z-10 mt-2 w-max origin-top-right rounded-lg bg-[#1f2732] text-white shadow-[rgba(0,0,0,0.5)_3px_3px_8px,_rgba(0,0,0,0.3)_6px_6px_28px] focus:outline-none">
          <div className="pb-4 pr-2 pt-1.5">
            <Menu.Item>
              <a
                href={REDIRECT_TO_TWITTER + userName}
                target="_blank"
                className="flex items-center gap-2 px-2 py-0.5 text-xs font-medium"
                rel="noreferrer"
              >
                <img src={Twitter} alt="Twitter" />
                Twitter
              </a>
            </Menu.Item>
            <Menu.Item>
              <a
                href={REDIRECT_TO_LINKEDIN + userName}
                target="_blank"
                className="flex items-center gap-2 px-2 py-0.5 text-xs font-medium"
                rel="noreferrer"
              >
                <img src={LinkedIn} alt="LinkedIn" />
                LinkedIn
              </a>
            </Menu.Item>
            <Menu.Item>
              <div
                tabIndex={0}
                role="menuitem"
                onKeyDown={() => {}}
                onClick={() => linkShare(`/user-profile/${userName}`)}
                className="flex cursor-pointer items-center gap-2 px-2 py-0.5 text-xxs font-medium"
              >
                <ClipboardDocumentCheckIcon className="mx-1.5 mt-1 h-5 w-5" />
                <p>Copy Profile Link</p>
              </div>
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default UserProfile;
